import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { FileText, Car, Clock, UserX, TrendingUp, CalendarDays, Euro, TrendingDown, Percent } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUT_BADGE = {
  "devis": "bg-amber-100 text-amber-800",
  "réservé": "bg-green-100 text-green-800",
  "annulé": "bg-red-100 text-red-700",
  "en_attente": "bg-orange-100 text-orange-800",
};

export default function AdminDashboard() {
  const { authHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/stats`, { headers: authHeaders })
      .then(r => setStats(r.data)).catch(() => setStats({}))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const cards = [
    { label: "TOTAL DEVIS", value: stats?.total || 0, icon: FileText, color: "text-[#D4A843]", bg: "bg-amber-50" },
    { label: "RÉSERVÉS", value: stats?.reserves || 0, icon: Car, color: "text-green-600", bg: "bg-green-50" },
    { label: "EN ATTENTE", value: stats?.en_attente || 0, icon: Clock, color: "text-orange-500", bg: "bg-orange-50" },
    { label: "ANNULÉS", value: stats?.annules || 0, icon: UserX, color: "text-red-500", bg: "bg-red-50" },
  ];

  const fmt = d => d ? d.split('-').reverse().join('/') : '—';

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">Tableau de bord</h1>
        <p className="text-gray-500 text-sm mt-1">Bienvenue dans votre espace d'administration</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        {cards.map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white p-6 shadow-sm" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase">{label}</p>
                <p className={`text-3xl font-bold mt-2 ${color}`}>{value}</p>
              </div>
              <div className={`w-10 h-10 ${bg} rounded-full flex items-center justify-center`}>
                <Icon size={18} className={color} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Financial KPIs */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-white p-6 shadow-sm border-l-4 border-blue-400">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase">CA Réservé</p>
              <p className="text-3xl font-bold mt-2 text-blue-600">{stats?.ca_reserve || 0}€</p>
            </div>
            <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
              <Euro size={18} className="text-blue-600" />
            </div>
          </div>
        </div>
        <div className="bg-white p-6 shadow-sm border-l-4 border-red-400">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase">Total Coûts</p>
              <p className="text-3xl font-bold mt-2 text-red-500">{stats?.total_couts || 0}€</p>
              <p className="text-xs text-gray-400 mt-1">sur devis avec coûts saisis</p>
            </div>
            <div className="w-10 h-10 bg-red-50 rounded-full flex items-center justify-center">
              <TrendingDown size={18} className="text-red-500" />
            </div>
          </div>
        </div>
        <div className={`bg-white p-6 shadow-sm border-l-4 ${(stats?.marge || 0) >= 0 ? "border-green-400" : "border-orange-400"}`}>
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-semibold text-gray-400 tracking-widest uppercase">Marge nette</p>
              <p className={`text-3xl font-bold mt-2 ${(stats?.marge || 0) >= 0 ? "text-green-600" : "text-orange-500"}`}>
                {stats?.marge || 0}€
              </p>
              <p className={`text-xs font-semibold mt-1 ${(stats?.marge || 0) >= 0 ? "text-green-500" : "text-orange-400"}`}>
                {stats?.taux_marge || 0}% de marge
              </p>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${(stats?.marge || 0) >= 0 ? "bg-green-50" : "bg-orange-50"}`}>
              <Percent size={18} className={(stats?.marge || 0) >= 0 ? "text-green-600" : "text-orange-500"} />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Chart */}
        <div className="bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp size={16} className="text-[#D4A843]" />
            <h3 className="font-bold text-gray-900">CA Réservé par mois</h3>
          </div>
          {stats?.revenue_chart?.some(d => d.ca > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.revenue_chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => [`${v}€`, "CA"]} />
                <Bar dataKey="ca" fill="#D4A843" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
              Aucune donnée disponible
            </div>
          )}
        </div>

        {/* Upcoming reservations */}
        <div className="bg-white p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-6">
            <CalendarDays size={16} className="text-[#D4A843]" />
            <h3 className="font-bold text-gray-900">Prochaines réservations</h3>
          </div>
          {stats?.prochaines?.length > 0 ? (
            <ul className="space-y-3">
              {stats.prochaines.map(d => (
                <li key={d.id} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{d.client_name}</p>
                    <p className="text-xs text-gray-400">{d.vehicle_name}</p>
                  </div>
                  <span className="text-sm font-bold text-[#D4A843]">{fmt(d.date_mariage)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="h-48 flex items-center justify-center text-gray-400 text-sm">
              Aucune réservation confirmée
            </div>
          )}
        </div>
      </div>

      {/* Last devis */}
      <div className="bg-white shadow-sm">
        <div className="p-6 border-b border-gray-50">
          <h3 className="font-bold text-gray-900">Derniers devis</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dashboard-devis-table">
            <thead>
              <tr className="border-b border-gray-100 text-xs text-gray-400 font-semibold tracking-wider uppercase">
                {["N° Devis", "Client", "Véhicule", "Date Mariage", "Montant", "Statut"].map(h => (
                  <th key={h} className="px-6 py-4 text-left">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stats?.derniers?.map(d => (
                <tr key={d.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 text-[#D4A843] font-medium">{d.numero}</td>
                  <td className="px-6 py-4 text-gray-900">{d.client_name}</td>
                  <td className="px-6 py-4 text-gray-600">{d.vehicle_name}</td>
                  <td className="px-6 py-4 text-gray-600">{fmt(d.date_mariage)}</td>
                  <td className="px-6 py-4 font-semibold">{d.montant}€</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-semibold rounded-sm ${STATUT_BADGE[d.statut] || "bg-gray-100 text-gray-700"}`}>
                      {(d.statut || "").charAt(0).toUpperCase() + (d.statut || "").slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
